/**
 * Les onglets d'une fiche de profil.
 *
 * POURQUOI CE FICHIER : la fiche câblait ses deux onglets en dur, avec trois
 * conditions dispersées — la barre entière disparaissait s'il n'y avait pas
 * de moments (`showMomentsTab`), le contenu Accueil était masqué par une
 * condition écrite à la main, et chaque module ajouté aurait demandé de
 * toucher les trois. Au troisième onglet, cette ligne devient illisible ;
 * au cinquième, dangereuse.
 *
 * LA RÈGLE D'AFFICHAGE, inchangée : un onglet apparaît quand la personne a
 * le module ET qu'elle y a mis du contenu. Posséder ne suffit pas — c'est le
 * contenu qui intéresse un visiteur, pas l'abonnement. Cette règle était
 * déjà celle de l'onglet Moments ; elle devient celle de tous.
 */

/**
 * Construit la liste des onglets visibles.
 *
 * @param {Array}  definitions  les onglets possibles, dans l'ordre d'affichage
 * @param {Set}    modules      les codes de modules accessibles au profil consulté
 * @param {object} contenus     { CODE_MODULE: nombre d'éléments }
 * @returns {Array} les onglets à rendre, chacun avec son compte
 *
 * Un onglet `toujours: true` (l'accueil) figure toujours en tête, sans
 * condition : une fiche a toujours quelque chose à montrer.
 */
export function ongletsVisibles(definitions, modules, contenus = {}) {
    if (!Array.isArray(definitions)) return [];

    const codesModules = modules instanceof Set ? modules : new Set(modules || []);
    // Une valeur par défaut ne protège pas d'un null EXPLICITE : l'appelant
    // qui passe le résultat d'un chargement raté transmet null, pas rien.
    const comptes = contenus || {};

    return definitions.filter((onglet) => {
        if (!onglet || !onglet.code) return false;
        if (onglet.toujours) return true;
        if (!onglet.moduleCode) return false;

        // Les deux conditions, dans cet ordre : le module, puis le contenu.
        if (!codesModules.has(onglet.moduleCode)) return false;

        const nombre = Number(comptes[onglet.moduleCode]) || 0;
        return nombre > 0;
    }).map((onglet) => {
        const nombre = Number(comptes[onglet.moduleCode]) || 0;
        return { ...onglet, nombre };
    });
}

/**
 * L'onglet réellement actif.
 *
 * Un onglet peut disparaître sous les pieds du visiteur : il consulte les
 * moments d'une personne, les données se rechargent, le module n'est plus
 * accessible. Sans ce garde, la fiche afficherait une page blanche — le
 * contenu de l'onglet demandé n'existe plus, et celui de l'accueil est
 * masqué parce qu'un autre onglet est « actif ».
 *
 * Rend donc toujours un onglet existant, en retombant sur le premier.
 */
export function ongletActif(onglets, demande) {
    if (!Array.isArray(onglets) || onglets.length === 0) return null;
    if (onglets.some((o) => o.code === demande)) return demande;
    return onglets[0].code;
}

/**
 * Vrai si la barre d'onglets mérite d'être affichée.
 *
 * Un seul onglet ne se choisit pas : afficher une barre à un bouton
 * demanderait au visiteur de comprendre une navigation qui n'existe pas.
 */
export function barreEstUtile(onglets) {
    if (!Array.isArray(onglets)) return false;
    return onglets.length > 1;
}
